import { zodResolver } from '@hookform/resolvers/zod'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import { ChevronDown } from 'lucide-react'
import { motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useForm, type Control, type FieldValues, type Path } from 'react-hook-form'
import { z } from 'zod'

import successLottie from 'src/shared/assets/lottie/success-animation.lottie'
import { LockFilled } from 'src/shared/components/icons/lock-filled'
import { RefreshIcon } from 'src/shared/components/icons/refresh-icon'
import { ThinXIcon } from 'src/shared/components/icons/toasts/thin-x-icon'
import { Form } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { Dialog, DialogContent, DialogTitle } from 'src/shared/components/ui/popups/dialog'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { cn } from 'src/shared/utils/cn'
import { formatCurrency } from 'src/shared/utils/format-currency'
import { ScrollBarTailwindClassesDefault } from 'src/shared/utils/tailwind-styles'

import {
  calculateMaximumAffordability,
  calculateTotalMonthlyPayment,
} from '../../consumers/components/mortgage-calculator/utils'

import StartJourneyButton from './start-journey/start-journey-button'

function DropdownHeader({
  title,
  isOpen,
  setIsOpen,
}: {
  title: string
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}) {
  return (
    <div className="mt-3">
      <button
        type="button"
        className="mb-1 flex w-full items-center justify-between gap-1"
        onClick={() => setIsOpen(!isOpen)}
      >
        <p className="text-base font-semibold leading-[1.2] tracking-[-0.16px] text-primary">{title}</p>
        <ChevronDown className={cn('size-6 transition-transform duration-200', isOpen && '-rotate-180')} />
      </button>
    </div>
  )
}

interface HomeCalculatorInputFormFieldProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label: string
  placeholder?: string
  type?: 'currency' | 'year' | 'percentage'
  maxLength?: number
  autoComplete?: string
  disabled?: boolean
}

function HomeCalculatorInputFormField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  type,
  maxLength,
  autoComplete,
  disabled,
}: HomeCalculatorInputFormFieldProps<T>) {
  const formatInputCurrency = (value: string) => {
    const numericValue = parseFloat(value)
    if (isNaN(numericValue)) return ''
    return `$${formatCurrency(numericValue)}`
  }

  const formatInputYear = (value: string) => {
    const numericValue = parseFloat(value)
    if (isNaN(numericValue)) return ''
    return `${numericValue}`
  }

  const formatInputPercentage = (value: string) => {
    return `${value}`
  }

  const getRawDigits = (value: string) => {
    const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''))
    if (isNaN(numericValue)) return undefined
    return numericValue
  }

  /**
   * Number with decimal. Keep only 3 decimal places.
   */
  const getRawFloat = (value: string) => {
    const numericValue = value.replace(/[^0-9.]/g, '')
    const splitValue = numericValue.split('.')
    if (splitValue.length > 1) {
      return splitValue[0] + '.' + splitValue[1].slice(0, 3)
    }
    return numericValue
  }

  return (
    <InputFormField
      control={control}
      name={name}
      label={label}
      placeholder={placeholder}
      className="space-y-1"
      fieldClassName="text-primary focus-visible:outline-none border-none text-[16px] translate-y-[22px] placeholder:translate-y-0 bg-transparent px-5"
      floatingLabelClassName="peer-focus:text-muted peer-placeholder-shown:text-sm pointer-events-none bg-transparent -translate-y-[1px] px-1 peer-focus:px-1 peer-focus:-translate-y-[-1px] peer-placeholder-shown:translate-y-[-42%]"
      fieldContainerClassName="bg-[#F8F7F5] rounded-[12px] h-[60px]"
      type={'text'}
      maxLength={maxLength}
      autoComplete={autoComplete}
      disabled={disabled}
      useNewErrorMessageComponent={true}
      RightIcon={disabled ? LockFilled : undefined}
      rightIconClassName={cn('right-5 text-primary w-[12px] h-[15px]')}
      customParser={type === 'percentage' ? getRawFloat : getRawDigits}
      customFormatter={
        type === 'currency' ? formatInputCurrency : type === 'year' ? formatInputYear : formatInputPercentage
      }
    />
  )
}

interface HomeCalculatorPopupProps {
  isOpen: boolean
  setOpen: (open: boolean) => void
}

const homeCalculatorSchema = z.object({
  annualIncome: z.number(),
  downPayment: z.number(),
  interestRate: z.string(),
  amortizationPeriod: z.number(),
  annualPropertyTaxes: z.number().optional(),
  annualHomeInsurance: z.number().optional(),
  monthlyHOA: z.number().optional(),
  creditCardPayments: z.number().optional(),
  carPayments: z.number().optional(),
  otherDebtPayments: z.number().optional(),
})

type HomeCalculatorFormValues = z.infer<typeof homeCalculatorSchema>

export default function HomeCalculatorPopup({ isOpen, setOpen }: HomeCalculatorPopupProps) {
  const [showPropertyCosts, setShowPropertyCosts] = useState(false)
  const [showMonthlyDebtPayments, setShowMonthlyDebtPayments] = useState(false)
  const [result, setResult] = useState<{ buyingPower: number; monthlyPayment: number } | null>(null)
  const { isMobile } = useBreakpoint()

  const form = useForm<HomeCalculatorFormValues>({
    resolver: zodResolver(homeCalculatorSchema),
    mode: 'onSubmit',
    defaultValues: {
      annualIncome: undefined,
      downPayment: undefined,
      interestRate: undefined,
      amortizationPeriod: undefined,
      annualPropertyTaxes: undefined,
      annualHomeInsurance: undefined,
      monthlyHOA: undefined,
      creditCardPayments: undefined,
      carPayments: undefined,
    },
  })

  useEffect(() => {
    if (isOpen) {
      form.reset()
      setShowPropertyCosts(false)
      setShowMonthlyDebtPayments(false)
    }
  }, [isOpen])

  const onSubmit = (data: HomeCalculatorFormValues) => {
    const buyingPower = calculateMaximumAffordability({
      annualIncome: data.annualIncome || 0,
      downPayment: data.downPayment || 0,
      interestRate: parseFloat(data.interestRate || '0'),
      amortizationYears: data.amortizationPeriod || 0,
      annualDebtPayments:
        (data.annualPropertyTaxes || 0) +
        (data.annualHomeInsurance || 0) +
        (data.monthlyHOA || 0) * 12 +
        (data.creditCardPayments || 0) * 12 +
        (data.carPayments || 0) * 12 +
        (data.otherDebtPayments || 0) * 12,
      dti: undefined,
    })

    const monthlyPayment = calculateTotalMonthlyPayment({
      homePrice: buyingPower,
      downPayment: data.downPayment || 0,
      interestRate: parseFloat(data.interestRate || '0'),
      amortizationYears: data.amortizationPeriod || 0,
    })

    setResult({ buyingPower, monthlyPayment })
  }

  const handleReset = () => {
    form.reset()
    setShowPropertyCosts(false)
    setShowMonthlyDebtPayments(false)
    setResult(null)
  }

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      <DialogContent
        hideClose={true}
        className={cn(
          'flex h-[100dvh] max-w-[100vw] flex-col justify-between overflow-y-auto px-0 py-0 text-left xs:h-full xs:max-h-[716px] xs:w-fit xs:min-w-[450px] xs:max-w-[90vw] xs:rounded-[40px] sm:rounded-[40px] md:px-0 md:pb-0 md:pt-0',
        )}
      >
        {result ? (
          <HomeCalculatorResult
            buyingPower={result.buyingPower}
            monthlyPayment={result.monthlyPayment}
            reset={handleReset}
            setOpen={setOpen}
          />
        ) : (
          <>
            <div
              className={cn(
                'flex flex-col px-[6%] pt-5 xs:px-[50px] xs:pt-[50px]',
                !isMobile && ScrollBarTailwindClassesDefault,
                'overflow-y-auto xs:max-h-[611px] xs:[&::-webkit-scrollbar-track]:my-10',
                'duration-300 ease-in-out animate-in fade-in-0',
              )}
            >
              {isMobile && (
                <div
                  onClick={() => setOpen(false)}
                  className={cn(
                    'mb-[22px] flex size-[40px] shrink-0 items-center justify-center rounded-full bg-[#F8F7F5] hover:cursor-pointer',
                  )}
                >
                  <ThinXIcon size={12} className="text-primary" />
                </div>
              )}
              <DialogTitle>
                <p className="mb-2 text-[26px] font-semibold leading-[1.2] tracking-[-0.26px] text-primary">
                  Check your buying power
                </p>
              </DialogTitle>
              <p className="mb-6 text-[15px] font-medium leading-[1.6] text-muted">
                Fill in your details to see what you could afford.
              </p>
              <Form {...form}>
                <form id="home-calculator-form" onSubmit={form.handleSubmit(onSubmit)} className="flex h-full flex-col">
                  <div className="flex max-h-full flex-col">
                    <div className="flex flex-col gap-[10px] pb-3">
                      <HomeCalculatorInputFormField
                        control={form.control}
                        name="annualIncome"
                        label="Annual income"
                        type="currency"
                      />
                      <HomeCalculatorInputFormField
                        control={form.control}
                        name="downPayment"
                        label="Down payment"
                        type="currency"
                      />
                      <HomeCalculatorInputFormField
                        control={form.control}
                        name="interestRate"
                        label="Interest rate (%)"
                        type="percentage"
                      />
                      <HomeCalculatorInputFormField
                        control={form.control}
                        name="amortizationPeriod"
                        label="Amortization period (years)"
                        type="year"
                      />
                    </div>
                    <div>
                      {/* Property costs Section */}
                      <DropdownHeader
                        title="Property costs"
                        isOpen={showPropertyCosts}
                        setIsOpen={setShowPropertyCosts}
                      />
                      <div
                        className={cn(
                          'mt-4 flex flex-col gap-[10px] overflow-hidden transition-all duration-300 ease-in-out',
                          showPropertyCosts ? 'mb-4 max-h-[500px] opacity-100' : 'max-h-0 opacity-0',
                        )}
                      >
                        <HomeCalculatorInputFormField
                          control={form.control}
                          name="annualPropertyTaxes"
                          label="Annual property taxes"
                          type="currency"
                        />
                        <HomeCalculatorInputFormField
                          control={form.control}
                          name="annualHomeInsurance"
                          label="Annual home insurance"
                          type="currency"
                        />
                        <HomeCalculatorInputFormField
                          control={form.control}
                          name="monthlyHOA"
                          label="Monthly HOA"
                          type="currency"
                        />
                      </div>
                    </div>
                    <div>
                      <DropdownHeader
                        title="Monthly debt payments"
                        isOpen={showMonthlyDebtPayments}
                        setIsOpen={setShowMonthlyDebtPayments}
                      />
                      <div
                        className={cn(
                          'mt-4 flex flex-col gap-[10px] overflow-hidden transition-all duration-300 ease-in-out',
                          showMonthlyDebtPayments ? 'mb-4 max-h-[500px] opacity-100' : 'max-h-0 opacity-0',
                        )}
                      >
                        <HomeCalculatorInputFormField
                          control={form.control}
                          name="creditCardPayments"
                          label="Credit card payments"
                          type="currency"
                        />
                        <HomeCalculatorInputFormField
                          control={form.control}
                          name="carPayments"
                          label="Car payments"
                          type="currency"
                        />
                        <HomeCalculatorInputFormField
                          control={form.control}
                          name="otherDebtPayments"
                          label="Other debt payments"
                          type="currency"
                        />
                        {/* Bottom spacer */}
                        <div className="h-[120px] w-full xs:h-[50px]"></div>
                      </div>
                    </div>
                    {/* Bottom spacer */}
                    <div className="h-6 w-full"></div>
                  </div>
                </form>
              </Form>
            </div>
            <div className="border-t border-[#EFEFEF] bg-white px-5 pb-[30px] pt-5 xs:px-[50px]">
              <button
                form="home-calculator-form"
                type="submit"
                className="h-[55px] w-full rounded-full bg-primary text-[15px] font-semibold text-white"
              >
                Calculate
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function HomeCalculatorResult({
  buyingPower,
  monthlyPayment,
  reset,
  setOpen,
}: {
  buyingPower: number
  monthlyPayment: number
  reset: () => void
  setOpen: (open: boolean) => void
}) {
  const { isMobile } = useBreakpoint()

  // A hack to lower the font size if there are too many digits.
  const numberOverflow = Math.floor(buyingPower).toString().length > 6
  const shouldLowerFontSize = numberOverflow && isMobile

  return (
    <div
      className={cn(
        'flex h-full flex-col items-center justify-between overflow-y-auto overflow-x-hidden px-5 pb-[52px] pt-5 text-center transition-all duration-300 ease-in-out xs:h-[716px] xs:px-[50px] xs:pt-[10px]',
        ScrollBarTailwindClassesDefault,
      )}
    >
      {isMobile ? (
        <div className="flex w-full flex-row justify-between px-1">
          <div
            onClick={() => setOpen(false)}
            className="flex size-[40px] shrink-0 items-center justify-center rounded-full bg-[#F8F7F5] hover:cursor-pointer"
          >
            <ThinXIcon size={12} className="text-primary" />
          </div>
          <div
            onClick={reset}
            className="flex size-[40px] shrink-0 items-center justify-center rounded-full bg-[#F8F7F5] hover:cursor-pointer"
          >
            <RefreshIcon size={isMobile ? 20 : 22} className="text-primary" />
          </div>
        </div>
      ) : (
        <div
          onClick={reset}
          className={cn(
            'absolute left-[27px] top-[29px] flex size-[45px] shrink-0 items-center justify-center rounded-full bg-[#F8F7F5] hover:cursor-pointer',
          )}
        >
          <RefreshIcon size={isMobile ? 20 : 22} className="text-primary" />
        </div>
      )}
      <div className="mb-12 flex w-full flex-1 flex-col items-center xs:mb-7">
        <div className="flex h-full flex-col items-center justify-center">
          <div className="relative size-[100px] shrink-0 xs:mt-3">
            <DotLottieReact src={successLottie} autoplay loop={false} segment={[0, 50]} speed={1} />
          </div>
          <DialogTitle>
            <p className="mb-2 text-[26px] font-semibold leading-[1.2] tracking-[-0.32px] text-primary xs:text-[32px]">
              Your buying power
            </p>
          </DialogTitle>
          <p className="mb-7 text-[15px] font-medium leading-[1.6] text-muted xs:text-[16px]">
            Based on the information you entered.
          </p>
          <motion.div
            className="overflow-hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            transition={{ duration: 0.6, ease: 'easeInOut', delay: 1.7 }}
          >
            <div className={cn('mb-7 rounded-[12px] bg-[#F8F7F5] px-10 py-5')}>
              <p
                className={cn(
                  'text-[46px] font-semibold leading-[1.2] tracking-[-0.46px] text-primary',
                  shouldLowerFontSize && 'text-[40px]',
                )}
              >
                ${formatCurrency(Math.floor(buyingPower))}
              </p>
            </div>
            <p className="mb-4 text-[15px] font-medium leading-[1.6] text-primary">or in monthly payments of</p>
            <div className="rounded-[12px] border border-[#EFEFEF] px-[25px] py-[19px]">
              <p
                className={cn(
                  'text-[36px] font-semibold leading-[1.2] tracking-[-0.36px] text-primary',
                  shouldLowerFontSize && 'text-[30px]',
                )}
              >
                ${formatCurrency(Math.floor(monthlyPayment))}
                <span className="text-[16px] font-medium leading-[1.2] tracking-[-0.16px] text-muted">{'/month'}</span>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
      <motion.div
        className="w-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, ease: 'easeInOut', delay: 2.8 }}
      >
        <StartJourneyButton
          className={cn('mx-auto h-[60px] w-full max-w-[320px] px-5 xs:h-[60px] xs:px-14 xs:text-[16px]')}
          trackingEvent={{ pageName: 'consumerHomePage', tag: 'hp_calculator_popup' }}
        />
      </motion.div>
    </div>
  )
}
